import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ChevronDown, ChevronRight, Plus, Trash2 } from 'lucide-react';
import {
  POS_PAYMENT_METHODS_DEFAULT,
  makePosPaymentMethod,
  posMethodFeeTypes,
  type PosPaymentMethod,
} from '@mlm/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  Checkbox,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import { getRoleOptions, getTenantFeatures, patchTenantFeatures } from '../lib/api';
import { hasPerm } from '../lib/auth-store';
import { ConfirmSaveButton } from '../components/confirm-save-button';
import { FundsPostsTo, useFundsPostsToVisible } from '../components/funds-posts-to';
import { MethodFeesEditor } from '../components/pos/method-fees-editor';
import { validateMethodFees } from '../components/pos/method-fees-validate';

/**
 * POS (Cashier) payment methods — admin-managed registry for the in-person
 * tender picker. Each row pairs a `tenderKind` (cash-with-change | exact | wallet)
 * with a wallet `currency` (for wallet methods), an optional per-role
 * restriction (`roleCodes` — empty means every cashier) and its own custom
 * `fees` (the old standalone POS custom fees page, now per method). Saving needs
 * the grantable `tenant.payment_methods.write` (super_admin passes implicitly).
 * Mirrors the typed-editor pattern in `ecom-payment-methods.tsx` /
 * `payout-deductions.tsx`.
 */
function clone<T>(v: T): T {
  return JSON.parse(JSON.stringify(v)) as T;
}

type RoleOption = { code: string; name: string };

/**
 * A method as edited on this page. `feesInherited` is UI-only (never saved):
 * the stored method had no `fees`, so its list was copied from the legacy global
 * `pos_custom_fees` and the card says so until the page is saved.
 */
type EditableMethod = PosPaymentMethod & { feesInherited?: boolean };

/** A GC/Coin wallet tender — a peso fee isn't denominable in scrip, so no fees. */
function isScripWallet(m: Pick<PosPaymentMethod, 'tenderKind' | 'currency'>): boolean {
  return m.tenderKind === 'wallet' && m.currency !== 'cash';
}

export function PosPaymentMethodsPage() {
  const qc = useQueryClient();
  const canWrite = hasPerm('tenant.payment_methods.write');
  const q = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  // Codes that exist SERVER-side. The "Posts to" picker writes posting rules
  // immediately, so a draft-only code must not get one (it would never match).
  const savedCodes = new Set(
    (q.data?.pos_payment_methods?.methods ?? []).map((x) => x.code.trim()),
  );
  // Role choices for the per-method "Allowed roles" picker. On error the picker
  // degrades to a read-only code list rather than blocking the whole editor.
  const rolesQ = useQuery({ queryKey: ['role-options'], queryFn: getRoleOptions });

  const [methods, setMethods] = useState<EditableMethod[]>([]);
  const [err, setErr] = useState<string | null>(null);
  // Same gate as the per-row "Posts to" picker — the hint and the pickers
  // appear/disappear together.
  const postsToVisible = useFundsPostsToVisible();

  useEffect(() => {
    if (!q.data) return;
    const features = q.data;
    const cfg = features.pos_payment_methods ?? POS_PAYMENT_METHODS_DEFAULT;
    const stored = Array.isArray(cfg.methods) ? cfg.methods : [];
    // A method saved before fees moved here has no `fees` and still charges the
    // legacy global list at the till (`posMethodFeeTypes`). Copy that list in so
    // the editor shows what is really charged; saving makes it the method's own.
    setMethods(
      stored.map((m) => {
        const fees = clone(posMethodFeeTypes(features, m));
        return {
          ...clone(m),
          fees,
          feesInherited: !Array.isArray(m.fees) && !isScripWallet(m) && fees.length > 0,
        };
      }),
    );
  }, [q.data]);

  // `roleCodes: []` and an absent field mean the same thing ("all cashiers") —
  // persist the canonical absent form so the stored config never carries an
  // empty list. `fees` is the opposite: always written explicitly, so a saved
  // method never falls back to the legacy list again, and always `[]` for a
  // GC/Coin wallet (the server refuses fees there).
  const normalizedMethods: PosPaymentMethod[] = methods.map((m) => ({
    code: m.code,
    label: m.label,
    tenderKind: m.tenderKind,
    currency: m.currency,
    enabled: m.enabled,
    ...(m.roleCodes && m.roleCodes.length > 0 ? { roleCodes: m.roleCodes } : {}),
    fees: isScripWallet(m) ? [] : (m.fees ?? []),
  }));

  const save = useMutation({
    meta: { successMessage: 'POS payment methods saved.' },
    mutationFn: () => patchTenantFeatures({ pos_payment_methods: { methods: normalizedMethods } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant-features'] }),
  });

  function updateMethod(i: number, patch: Partial<EditableMethod>) {
    setMethods((ms) => ms.map((m, idx) => (idx === i ? { ...m, ...patch } : m)));
  }
  function addMethod() {
    setMethods((ms) => [...ms, makePosPaymentMethod()]);
  }
  function removeMethod(i: number) {
    setMethods((ms) => ms.filter((_, idx) => idx !== i));
  }

  function validate(): string | null {
    const codes = methods.map((m) => m.code.trim());
    let msg: string | null = null;
    if (codes.some((c) => c === '')) msg = 'Every method needs a code.';
    else if (codes.some((c) => !/^[a-z0-9_]+$/.test(c)))
      msg = 'Codes must be lowercase letters, digits, or underscores.';
    else if (new Set(codes).size !== codes.length) msg = 'Method codes must be unique.';
    else if (methods.some((m) => m.label.trim() === '')) msg = 'Every method needs a label.';
    // Cash/exact tenders are always cash-denominated; only a wallet tender may
    // debit a non-cash (GC/Coin) balance. A wallet method may debit the CASH
    // wallet too — the buyer pays the normal cash total from their cash balance.
    else if (methods.some((m) => m.tenderKind !== 'wallet' && m.currency !== 'cash'))
      msg = 'Only wallet methods can use a GC/Coin currency — set cash/exact methods to Cash.';
    else if (!methods.some((m) => m.enabled))
      msg = 'Enable at least one method — the cashier needs a way to take payment.';
    // Each method's own fee list. A GC/Coin wallet's fees are dropped on save,
    // so there is nothing of theirs to check.
    if (!msg) {
      for (const m of methods) {
        if (isScripWallet(m)) continue;
        const feeMsg = validateMethodFees(m.fees ?? []);
        if (feeMsg) {
          msg = `${m.label.trim() || m.code}: ${feeMsg}`;
          break;
        }
      }
    }
    setErr(msg);
    return msg;
  }

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">POS payment methods</h1>
        <p className="text-sm text-muted-foreground">
          The tender options the cashier picks at checkout. <b>Cash</b> takes an amount tendered and
          gives change; <b>Exact</b> requires the tendered amount to equal the total (e.g. a card
          swipe, no change); <b>Wallet</b> debits the buyer slot's balance in the chosen currency —
          Cash (pays the normal cash total from the member's cash wallet), GC, or Coin. Each sale
          records the method for the daily-sales breakdown.
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Each method also carries its own <b>fees</b>, the charges a cashier can add on top of a
          sale paid with it (e.g. a 3% surcharge on Card, delivery on Cash). The cashier picks from
          the fees of the method being paid with, applied top to bottom on the running total. GC and
          Coin payments never carry fees.
        </p>
        {!canWrite && (
          <p className="text-xs text-muted-foreground mt-1">
            Read-only. Saving needs the &ldquo;Edit payment methods &amp; POS fees&rdquo; permission
            (<span className="font-mono">tenant.payment_methods.write</span>).
          </p>
        )}
        {postsToVisible && (
          <p className="text-xs text-muted-foreground mt-1">
            The picker sets the any-branch rule. Per-branch tills (e.g. cash into each branch's
            drawer account) are set on Cash &amp; Bank → Auto-posting rules. Rules save immediately
            and key on the method code.
          </p>
        )}
      </div>

      {q.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          {methods.length === 0 && (
            <Card>
              <CardContent className="py-6 text-sm text-muted-foreground">
                No payment methods yet.{' '}
                {canWrite
                  ? 'Add one below.'
                  : 'An admin with the payment-methods edit permission can add them.'}{' '}
                The cashier can't take payment until at least one method is enabled.
              </CardContent>
            </Card>
          )}

          {methods.map((m, i) => (
            <MethodCard
              key={i}
              method={m}
              canWrite={canWrite}
              roleOptions={rolesQ.data}
              roleOptionsFailed={rolesQ.isError}
              savedCodes={savedCodes}
              onChange={(patch) => updateMethod(i, patch)}
              onRemove={() => removeMethod(i)}
            />
          ))}

          {canWrite && (
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" onClick={addMethod}>
                <Plus className="h-4 w-4" /> Add method
              </Button>
              <ConfirmSaveButton
                size="sm"
                label="Save"
                title="Save POS payment methods"
                current={q.data?.pos_payment_methods}
                next={{ methods: normalizedMethods }}
                loading={save.isPending}
                validate={validate}
                onInvalid={setErr}
                onConfirm={() => save.mutateAsync()}
              />
              {save.isSuccess && !save.isPending && (
                <span className="text-xs text-success">Saved.</span>
              )}
              {err && <span className="text-xs text-destructive">{err}</span>}
              {save.error && (
                <span className="text-xs text-destructive">Save failed. Try again.</span>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}

function MethodCard({
  method: m,
  canWrite,
  roleOptions,
  roleOptionsFailed,
  savedCodes,
  onChange,
  onRemove,
}: {
  method: EditableMethod;
  canWrite: boolean;
  /** undefined while loading; the picker renders once options arrive. */
  roleOptions?: RoleOption[];
  /** Options fetch failed — degrade to a read-only code list. */
  roleOptionsFailed: boolean;
  /** Codes in the SAVED registry — a draft code must not write a posting rule. */
  savedCodes: Set<string>;
  onChange: (patch: Partial<EditableMethod>) => void;
  onRemove: () => void;
}) {
  const isWallet = m.tenderKind === 'wallet';
  // Collapsed by default: most edits on this page are tender/roles, and a long
  // fee list would push the next method off screen.
  const [feesOpen, setFeesOpen] = useState(false);
  const fees = m.fees ?? [];
  // Accounting off / no view.accounting → no "Posts to" block at all: the card
  // must look exactly as it did before the affordance existed.
  const postsToVisible = useFundsPostsToVisible();
  // Cashier phase 5: a till top-up paid with this method posts under its own
  // source, so it needs its own rule. Same cached features query as the page.
  const featuresQ = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  const takesTopups =
    featuresQ.data?.pos_topup_enabled === true &&
    (m.tenderKind === 'cash' ||
      (m.tenderKind === 'exact' && featuresQ.data?.pos_topup_exact_methods_enabled === true));
  const selectedRoles = m.roleCodes ?? [];
  // Saved codes whose role has since been deleted/renamed — surfaced as a
  // warning chip (they'd silently keep restricting the tender otherwise).
  const orphanRoles = roleOptions
    ? selectedRoles.filter((c) => !roleOptions.some((r) => r.code === c))
    : [];
  const setRoles = (next: string[]) => onChange({ roleCodes: next });
  return (
    <Card>
      <CardContent className="py-4 space-y-3">
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-4">
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Code</label>
            <Input
              value={m.code}
              disabled={!canWrite}
              placeholder="gcash"
              onChange={(e) => onChange({ code: e.target.value.trim() })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Label</label>
            <Input
              value={m.label}
              disabled={!canWrite}
              placeholder="GCash"
              onChange={(e) => onChange({ label: e.target.value })}
            />
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Tender</label>
            <Select
              value={m.tenderKind}
              disabled={!canWrite}
              onValueChange={(v) => {
                const tenderKind = v as PosPaymentMethod['tenderKind'];
                // A cash/exact tender is always cash. A wallet tender keeps its
                // currency (cash / gc / coin) — cash-wallet debits the cash balance.
                onChange({
                  tenderKind,
                  currency: tenderKind === 'wallet' ? m.currency : 'cash',
                });
              }}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash (with change)</SelectItem>
                <SelectItem value="exact">Exact (no change)</SelectItem>
                <SelectItem value="wallet">Wallet debit</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1">
            <label className="text-xs text-muted-foreground">Currency</label>
            <Select
              value={m.currency}
              disabled={!canWrite || !isWallet}
              onValueChange={(v) => onChange({ currency: v as PosPaymentMethod['currency'] })}
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="cash">Cash</SelectItem>
                <SelectItem value="gc">GC</SelectItem>
                <SelectItem value="coin">Coin</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>
        {postsToVisible && (
          <div className="space-y-1 sm:max-w-xs">
            <label className="text-xs text-muted-foreground">Posts to</label>
            {isWallet ? (
              <p className="text-sm text-muted-foreground">Never posts — wallet tender</p>
            ) : (
              <FundsPostsTo source="pos_sale" code={m.code} saved={savedCodes.has(m.code.trim())} />
            )}
          </div>
        )}
        {postsToVisible && takesTopups && (
          <div className="space-y-1 sm:max-w-xs">
            <label className="text-xs text-muted-foreground">Top-ups at the till post to</label>
            <FundsPostsTo
              source="wallet_topup_counter"
              code={m.code}
              saved={savedCodes.has(m.code.trim())}
            />
          </div>
        )}
        <div className="space-y-1">
          <label className="text-xs text-muted-foreground">Allowed roles</label>
          {roleOptionsFailed ? (
            // Options unavailable — show what's stored, read-only, rather than
            // hiding a live restriction or letting a blind edit clobber it.
            <p className="text-xs text-muted-foreground">
              Couldn't load the role list.{' '}
              {selectedRoles.length > 0 ? (
                <>
                  Restricted to: <span className="font-mono">{selectedRoles.join(', ')}</span>
                </>
              ) : (
                'Open to all cashiers.'
              )}
            </p>
          ) : (
            <div className="flex flex-wrap items-center gap-2">
              {!roleOptions && (
                <span className="text-xs text-muted-foreground">Loading roles…</span>
              )}
              {(roleOptions ?? []).map((r) => {
                const on = selectedRoles.includes(r.code);
                return (
                  <label
                    key={r.code}
                    className="flex items-center gap-1.5 rounded-full border border-input px-2.5 py-1 text-xs"
                  >
                    <Checkbox
                      checked={on}
                      disabled={!canWrite}
                      onCheckedChange={(v) =>
                        setRoles(
                          v === true
                            ? [...selectedRoles, r.code]
                            : selectedRoles.filter((c) => c !== r.code),
                        )
                      }
                    />
                    {r.name}
                  </label>
                );
              })}
              {orphanRoles.map((code) => (
                <Badge key={code} variant="warning" className="gap-1 normal-case">
                  <span className="font-mono">{code}</span> (deleted role)
                  {canWrite && (
                    <button
                      type="button"
                      aria-label={`Remove ${code}`}
                      className="ml-0.5 leading-none"
                      onClick={() => setRoles(selectedRoles.filter((c) => c !== code))}
                    >
                      ×
                    </button>
                  )}
                </Badge>
              ))}
              {roleOptions && selectedRoles.length === 0 && (
                <span className="text-xs text-muted-foreground">All cashiers</span>
              )}
            </div>
          )}
          <p className="text-[11px] text-muted-foreground">
            Tick roles to restrict who can take this tender; leave every box unticked to allow all
            cashiers. A restricted method is hidden from other tills and rejected at checkout.
          </p>
        </div>
        {isScripWallet(m) ? (
          <p className="text-xs text-muted-foreground">
            Fees: none. A GC or Coin payment can&apos;t carry a peso fee, so this method offers no
            fees at the till.
          </p>
        ) : (
          <div className="rounded-md border">
            <button
              type="button"
              className="flex w-full items-center gap-1.5 px-3 py-2 text-left text-sm font-medium"
              aria-expanded={feesOpen}
              onClick={() => setFeesOpen((o) => !o)}
            >
              {feesOpen ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
              Fees
              <span className="text-xs font-normal text-muted-foreground">({fees.length})</span>
            </button>
            {m.feesInherited && (
              <p className="px-3 pb-2 text-[11px] text-muted-foreground">
                Inherited from the old POS custom fees list, saving makes it this method&apos;s own.
              </p>
            )}
            {feesOpen && (
              <div className="border-t px-3 py-3">
                <MethodFeesEditor
                  fees={fees}
                  canWrite={canWrite}
                  onChange={(next) => onChange({ fees: next })}
                />
              </div>
            )}
          </div>
        )}
        <div className="flex items-center justify-between">
          <label className="flex items-center gap-2 text-sm">
            <Checkbox
              checked={m.enabled}
              disabled={!canWrite}
              onCheckedChange={(v) => onChange({ enabled: v === true })}
            />
            Enabled
          </label>
          {canWrite && (
            <Button variant="ghost" size="sm" onClick={onRemove}>
              <Trash2 className="h-4 w-4" /> Remove
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
